import { Grid2, MenuItem, TextField } from '@mui/material';
import { WheelEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type AcreageAndContainmentFieldsProps = {
  isPrescribed: boolean;
};

const getFootageInAcres = (length: number, width: number): number => {
  return parseFloat(((length * width) / 43560).toFixed(2));
};

const floatFieldProps = {
  type: 'number',
  slotProps: {
    htmlInput: {
      pattern: '[0-9]+([.,][0-9]+)?',
      inputMode: 'decimal',
      onWheel: (e: WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
    },
  },
};

const intFieldProps = {
  type: 'number',
  slotProps: {
    htmlInput: {
      pattern: '[0-9]*',
      inputMode: 'numeric',
      onWheel: (e: WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
    },
  },
};

export const AcreageAndContainmentFields = (
  props: AcreageAndContainmentFieldsProps,
): JSX.Element => {
  const { isPrescribed } = props;
  const { control, formState, watch, setValue } = useFormContext();
  const { t } = useTranslation();

  const [sizeUnit, feetLength, feetWidth] = watch([
    'sizeUnit',
    'feetLength',
    'feetWidth',
  ]);
  const isUnitFeet = sizeUnit === 'feet';

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: isUnitFeet ? 4 : 12, md: 2 }}>
        <Controller
          name="sizeUnit"
          control={control}
          render={({ field }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                select
                id="field-control-acreage-unit"
                label={t('createEditGeoEvent.inputs.acreage.unit')}
                fullWidth
                inputRef={ref}
                {...muiFieldProps}
              >
                <MenuItem value="acres">
                  {t('createEditGeoEvent.inputs.acreage.acres')}
                </MenuItem>
                <MenuItem value="feet">
                  {t('createEditGeoEvent.inputs.acreage.feet')}
                </MenuItem>
              </TextField>
            );
          }}
        />
      </Grid2>

      {isUnitFeet && (
        <>
          <Grid2 size={{ xs: 4, md: 2 }}>
            <Controller
              name="feetLength"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, value, onChange, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-acreage-feet-length"
                    label={t('createEditGeoEvent.inputs.acreage.length')}
                    fullWidth
                    {...muiFieldProps}
                    value={value || ''}
                    onChange={(e) => {
                      const numVal = e.target.value
                        ? parseInt(e.target.value, 10)
                        : null;
                      onChange(numVal);
                      if (!numVal) setValue('acreage', null);
                      if (numVal && feetWidth) {
                        setValue(
                          'acreage',
                          getFootageInAcres(numVal, feetWidth),
                        );
                      }
                    }}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    disabled={formState.isSubmitting}
                    {...intFieldProps}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 4, md: 2 }}>
            <Controller
              name="feetWidth"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, value, onChange, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-acreage-feet-width"
                    label={t('createEditGeoEvent.inputs.acreage.width')}
                    fullWidth
                    {...muiFieldProps}
                    value={value || ''}
                    onChange={(e) => {
                      const numVal = e.target.value
                        ? parseInt(e.target.value, 10)
                        : null;
                      onChange(numVal);
                      if (!numVal) setValue('acreage', null);
                      if (numVal && feetLength) {
                        setValue(
                          'acreage',
                          getFootageInAcres(feetLength, numVal),
                        );
                      }
                    }}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    disabled={formState.isSubmitting}
                    {...intFieldProps}
                  />
                );
              }}
            />
          </Grid2>
        </>
      )}

      <Grid2 size={{ xs: 12, md: isUnitFeet ? 3 : 5 }}>
        <Controller
          name="acreage"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, value, onChange, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-acreage"
                label={t('createEditGeoEvent.inputs.acreage.label')}
                fullWidth
                {...muiFieldProps}
                value={value ?? ''}
                onChange={(e) => {
                  onChange(e.target.value ? Number(e.target.value) : null);
                  if (feetLength) setValue('feetLength', null);
                  if (feetWidth) setValue('feetWidth', null);
                }}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={formState.isSubmitting || isUnitFeet}
                {...floatFieldProps}
              />
            );
          }}
        />
      </Grid2>

      <Grid2 size={{ xs: 12, md: isUnitFeet ? 3 : 5 }}>
        <Controller
          name="containment"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, value, onChange, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-containment"
                label={t('createEditGeoEvent.inputs.containment.label')}
                fullWidth
                {...muiFieldProps}
                value={value ?? ''}
                onChange={(e) =>
                  onChange(e.target.value ? parseInt(e.target.value, 10) : null)
                }
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={formState.isSubmitting || isPrescribed}
                {...intFieldProps}
              />
            );
          }}
        />
      </Grid2>
    </Grid2>
  );
};
